import { arrayOf, bool, string, func, shape } from 'prop-types';
import React, { useState } from 'react';
import { Mutation } from 'react-apollo';

import RecipeRating from 'components/RecipeRating';
import RecipeBoxTagForm from 'components/RecipeBoxTagForm';
import Error from 'components/Error';
import Trash from 'components/Icons/Trash';
import { MUTATION } from 'components/RecipeRatingsDataProvider';

import {
  updateRecipeInCache,
  removeRecipeFromCache,
  updateRatingInCache
} from './helpers';

import styles from './styles';

export function RecipeBoxRecipeEditForm(props) {
  const {
    url,
    headline,
    recipeId,
    userRecipeId,
    userTagsIds,
    userNotes,
    onRecipeBoxDataChange,
    onUpdateRecipeInBox,
    onRemoveRecipeFromBox,
    toggleEditForm,
    setIsDeleted,
    setIsUpdating,
    isUpdating,
    ratingsData,
    userRatingsData
  } = props;

  const [notes, setNotes] = useState(userNotes);
  const [error, setError] = useState(null);

  const handleNotesChange = e => {
    e.preventDefault();
    setNotes(e.target.value);
  };

  function handleFormSubmit(e) {
    e.preventDefault();
    setIsUpdating(true);
    setError(null);

    onUpdateRecipeInBox({
      variables: {
        input: {
          userRecipeId,
          tagsIds: userTagsIds,
          notes: notes.trim()
        }
      },
      update: updateRecipeInCache(userRecipeId)
    })
      .then((_, err) => {
        if (err) {
          setError(err);
          setIsUpdating(false);
          return;
        }
        setIsUpdating(false);
      })
      .catch(err => {
        setError(err);
        setIsUpdating(false);
      });
  }

  function handleRecipeRemoval() {
    if (
      // eslint-disable-next-line
      window.confirm(
        'Are you sure you want to remove this Recipe from your Box?'
      )
    ) {
      setIsUpdating(true);
      setError(null);

      onRemoveRecipeFromBox({
        variables: {
          input: { userRecipeId }
        },
        update: removeRecipeFromCache(userRecipeId)
      })
        .then((_, err) => {
          if (err) {
            setError(err);
            setIsUpdating(false);
            return;
          }
          // Mark this teaser as deleted so we can hide it on the UI
          setIsDeleted(true);
          // Callback to ask Algolia's client to clear the cache
          onRecipeBoxDataChange();
          setIsUpdating(false);
          toggleEditForm();
        })
        .catch(err => {
          setIsUpdating(false);
          // eslint-disable-next-line no-alert
          window.alert(
            `There was a problem deleting this Recipe from the Box: ${err.message}`
          );
        });
    }
  }

  return (
    <div className="TeaserRecipeCard__editInfo">
      <h3>{headline}</h3>
      <button
        onClick={handleRecipeRemoval}
        className="TeaserRecipeCard__deleteRecipeFromBox"
      >
        <Trash />
        Remove
      </button>
      <RecipeBoxTagForm
        userRecipeId={userRecipeId}
        recipeId={recipeId}
        tagTitle="Add / Edit Tags"
      />
      <div className="TeaserRecipeCard__editInfoRating">
        <Mutation mutation={MUTATION}>
          {(mutate, { loading: ratingLoading }) => (
            <RecipeRating
              ratingsData={ratingsData}
              userRatingsData={userRatingsData}
              rateRecipeIsLoading={ratingLoading}
              recipeId={parseInt(recipeId, 10)}
              rateRecipe={userRating =>
                mutate({
                  variables: {
                    recipeId: parseInt(recipeId, 10),
                    userRating
                  },
                  update: updateRatingInCache
                })
              }
              placement="recipe box"
            />
          )}
        </Mutation>
      </div>
      <form
        className="TeaserRecipeCard__editInfoNotes"
        data-gtm-trigger="recipe-box-save-notes"
        data-gtm-url={url}
        onSubmit={handleFormSubmit}
      >
        <label htmlFor={`TeaserRecipeCard__notesTextarea-${userRecipeId}`}>
          Notes
          <textarea
            id={`TeaserRecipeCard__notesTextarea-${userRecipeId}`}
            name="notes"
            rows="3"
            value={notes}
            onChange={handleNotesChange}
          />
        </label>
        <button
          type="submit"
          disabled={notes === userNotes || notes.trim() === '' || isUpdating}
          required
        >
          Save Notes
        </button>
        {error && <Error message={error.message} />}
      </form>
      <button
        className="TeaserRecipeCard__editInfoButton"
        onClick={toggleEditForm}
      >
        Done
      </button>
      <style jsx>{styles}</style>
    </div>
  );
}

RecipeBoxRecipeEditForm.propTypes = {
  url: string.isRequired,
  headline: string.isRequired,
  recipeId: string,
  userRecipeId: string,
  userTagsIds: arrayOf(string),
  userNotes: string,
  onRecipeBoxDataChange: func.isRequired,
  onUpdateRecipeInBox: func.isRequired,
  onRemoveRecipeFromBox: func.isRequired,
  toggleEditForm: func.isRequired,
  setIsDeleted: func.isRequired,
  setIsUpdating: func.isRequired,
  isUpdating: bool,
  ratingsData: shape({}),
  userRatingsData: shape({})
};

RecipeBoxRecipeEditForm.defaultProps = {
  recipeId: null,
  userRecipeId: null,
  userNotes: null,
  userTagsIds: [],
  isUpdating: false,
  ratingsData: undefined,
  userRatingsData: undefined
};

export default RecipeBoxRecipeEditForm;
